// SEQ-180 P&L analytics aggregation: a pure, grain-agnostic reproduction of
// src/services/pnlCalculationService.ts's calculateTotalsPnl semantics,
// generalised to buckets that can span one project, one month, one
// project-month, or the whole company.
//
// This module has no React import and no supabase import. It is proven
// against pnlCalculationService.ts directly: pnlAnalytics.test.ts imports
// calculateMonthlyPnl/calculateTotalsPnl from that path, which now
// `export * from '../lib/pnl/pnlCalculationCore'`. The service file's own
// runtime code never depended on React or supabase (only an erased
// `import type { PnlCategory }`), which a direct `deno run` proved. The
// dependency that mattered was transitive: Deno's type-checker still resolves
// PnlCategory's declaration by loading the whole of usePnlCategories.ts,
// which imports ToastModal.tsx (`document`) and supabase.ts
// (`import.meta.env`), neither available under Deno. pnlCalculationCore.ts
// (see its header comment) declares a structurally identical PnlCategory
// locally to break that link, with zero behaviour change to the P&L tab.
//
// WHY A REGISTRY INSTEAD OF HARDCODED CATEGORY NAMES. The existing engine
// hardcodes two name sets: `MULTIPLIED_WAGES = new Set(['Wages', 'Extra
// wages'])` (pnlCalculationService.ts:133) and the string 'Patching'
// (useDashboardPnl.ts:256). Both break silently if a category is renamed in
// EditExpenseCategoriesModal, which is exactly the AC10/AC11 defect the
// SEQ-180 plan calls out (map section 10, item 5). This module reads the same
// business rules off registry metadata (`deductible`, `multiplierApplies`,
// `group`, `unit`) instead of matching on display strings, so a renamed or a
// newly added category needs a registry row, not a code change.
//
// WHY THE WAGES MULTIPLIER IS APPLIED PER PROJECT, THEN SUMMED, BUT NEVER
// WRITTEN BACK INTO `labour.base_wages` / `labour.extra_wages`. A bucket can
// span more than one project (e.g. rollup grain 'month' or 'company'), and
// `wages_multiplier` is a per-project setting (project_financial_settings).
// Applying one multiplier to a multi-project bucket's raw wages total would
// silently use the wrong project's rate for every project but one. So the
// multiplier is applied to each project's own base_wages + extra_wages sum
// before that project's contribution feeds any calculated figure. Because
// multiplication distributes over addition, this is mathematically identical
// to summing raw wages across a whole year and multiplying once (as
// calculateTotalsPnl does for a single project), which is what makes twelve
// monthly buckets' calculated.* figures sum exactly to the annual bucket (see
// pnlAnalytics.test.ts).
//
// Round 3 correction (code review, parity harness divergence 2): this used to
// multiply `labour.base_wages`/`labour.extra_wages` in `metrics` itself, so
// the same key meant "raw" on the SQL side and "multiplied" here (SQL
// 124,021.10 vs this engine's old 130,146.46 on the harness's live fixture).
// Every input key in `metrics` is now always the raw summed figure, exactly
// like the RPC. The multiplier is applied only inside the calculated figures
// that need it (`calculated.total_cost`, `calculated.net_profit`,
// `calculated.wages_percentage`, `calculated.overtime_pct_wages`), via a
// bucket-level running total tracked separately from `metrics` (see
// `bucketMultipliedWagesTotal` below).
//
// WHY RATIOS RETURN null ON A ZERO DENOMINATOR. pnlCalculationService.ts
// returns 0 for a zero-income month, correct for a table cell that must show
// something. The SEQ-180 plan (section 6.5) deliberately diverges here: a
// chart plotting a null figure can omit the point, while a plotted 0 reads as
// "no profit" rather than "no data". The reconciliation test therefore only
// asserts parity on non-zero-income fixtures, exactly as the plan specifies.

import { wagesMultiplierAppliesAtGrain } from "./wagesMultiplierGrain";

/** One fact: a single metric's value for one project in one calendar month. */
export interface PnlAnalyticsInputRow {
  projectId: string;
  /** ISO 'YYYY-MM-01'. */
  periodMonth: string;
  metricKey: string;
  value: number;
  /**
   * Optional. `computePnlBuckets` is single-organisation by design (see its
   * doc comment); when rows carry this, the engine throws if more than one
   * distinct organisation is present rather than merging two tenants into
   * one bucket, which the RPC never does (it folds organization_id into
   * every bucket key).
   */
  organizationId?: string;
}

/**
 * SEQ-182: the full unit vocabulary a metric can carry. Only 'currency'
 * inputs can ever contribute to a dollar total (enforced structurally, see
 * `resolveFinancialInclusion` below). `currency_per_hour` is deliberately its
 * own unit rather than `currency`: if a rate were typed as currency, the
 * widget compatibility check in pnlWidgetSchema.ts would let a dollar total
 * and a dollar-per-hour rate share one axis, which is exactly the class of
 * error AC10 exists to stop.
 */
export type PnlUnit = "currency" | "percent" | "hours" | "count" | "ratio" | "currency_per_hour";

/**
 * SEQ-182: what a metric is FOR, distinct from `kind` (which only answers
 * whether a fact row exists for it, or the value is derived). 'financial' for
 * currency amounts that participate in the P&L, 'operational' for
 * non-currency quantities, 'calculated' for ratios/rates/percentages derived
 * from other metrics. `calculated.total_income` / `total_cost` / `net_profit`
 * are `kind: 'calculated'` but `metricRole: 'financial'`: they are derived,
 * but they are still dollar totals, not ratios.
 */
export type PnlMetricRole = "financial" | "operational" | "calculated";

/**
 * How a metric rolls up across fact rows or buckets.
 *
 * SEQ-182 Round 4 (security audit) narrowed this to `sum | calculated`
 * because the RPC silently summed an `avg`-tagged metric. SEQ-180 phase 2
 * (plan section 6) widens it back to the full six values because
 * `pnl_analytics_query` v3 now executes each of the five folds for
 * `kind: 'input'` metrics, and `computePnlBuckets` below implements the same
 * five folds so parity holds. `calculated` stays reserved for a derived
 * ratio or rate (sum-then-divide, never re-aggregated across buckets).
 *
 * Fold semantics (section 6.2), all over CELLS, where one cell is one
 * (projectId, periodMonth, metricKey) with at least one fact row:
 * - `sum`: total across cells; no cell reports 0.
 * - `avg`: unweighted mean across cells; no cell reports null.
 * - `min` / `max`: lowest / highest cell; no cell reports null.
 * - `latest`: the sum of the cells at the greatest periodMonth in the
 *   bucket (a snapshot of one date, section 6.3), reported with that date
 *   under `metricsAsAt`; no cell reports null.
 */
export type PnlAggregationMethod = "sum" | "avg" | "min" | "max" | "latest" | "calculated";

/** A row's classification, section, and the handful of business rules that vary per metric. */
export interface PnlMetricDefinition {
  metricKey: string;
  section: "revenue" | "budget" | "expense" | "labour" | "calculated";
  kind: "input" | "calculated";
  /** currency | percent | hours | count | ratio | currency_per_hour. Defaults to 'currency'. */
  unit?: PnlUnit;
  /**
   * Only meaningful for kind 'input'. Whether this metric reduces net profit.
   * Defaults to true. Set false for `expense.materials` (its dollar value is
   * tracked but the 30% budget line replaces it as the deduction, matching
   * pnlCalculationService.ts:83-88) and for `labour.overtime` (a KeyPay-derived
   * figure that is not part of the P&L's own deductions, so including it
   * would break reconciliation with the project P&L tab, AC02).
   */
  deductible?: boolean;
  /**
   * Only meaningful for kind 'input' in section 'labour'. When true, this
   * metric's per-project sum feeds `calculated.total_cost` /
   * `calculated.net_profit` / `calculated.wages_percentage` /
   * `calculated.overtime_pct_wages` multiplied by that project's wages
   * multiplier. The value reported under this metric's own key in `metrics`
   * stays the raw, unmultiplied sum (Round 3 fix; see the file header). Set
   * on `labour.base_wages` and `labour.extra_wages` only, matching
   * MULTIPLIED_WAGES in pnlCalculationService.ts:133.
   */
  multiplierApplies?: boolean;
  /**
   * The legacy three-way grouping pnlCalculationService.ts used
   * (PnlCategory.group), needed only to reproduce wagesPercent and
   * materialsPercent. 'wages' is implied by section 'labour' and is not set
   * here; 'materials' marks the three deductible Materials-group expense
   * keys (engineering, handrails/stairs, misc materials).
   */
  group?: "materials" | "other";
  /** SEQ-182. See `PnlMetricRole`. Defaults to 'financial' when unset (`resolveMetricRole`). */
  metricRole?: PnlMetricRole;
  /**
   * SEQ-182. Whether this metric's value can ever be added into a financial
   * P&L total (`calculated.total_cost` / `calculated.net_profit` / any other
   * dollar sum). Distinct from `deductible`, which is only meaningful once a
   * metric IS financially included: `expense.materials` is
   * `financialInclusion: true, deductible: false` (tracked, but the budget
   * line replaces it as the deduction), while `labour.overtime` is
   * `financialInclusion: false` (currency, but outside the typed P&L model
   * entirely). Defaults to `true` for a currency metric and `false` for
   * everything else (`resolveFinancialInclusion`), so a non-currency metric
   * never needs to name itself to be excluded from a dollar total.
   * `assertRegistryConsistent` enforces that a non-currency metric can never
   * set this to `true`.
   */
  financialInclusion?: boolean;
  /**
   * SEQ-182. See `PnlAggregationMethod`. Defaults to 'calculated' for a
   * calculated metric, 'sum' for an input metric (`resolveAggregationMethod`).
   */
  aggregationMethod?: PnlAggregationMethod;
  /** SEQ-182. Display decimal places for `formatMetricValue`. Optional: that module has its own per-unit default. */
  decimals?: number;
  /**
   * SEQ-182 Round 6 (browser E2E finding). Marks an input metric as sourced
   * from payroll (KeyPay via the overtime/ordinary hours matview), data-driven
   * rather than a hardcoded key list so a caller cannot silently diverge from
   * this flag by matching on `metricKey` strings elsewhere. Only meaningful
   * for `kind: 'input'`. `computePnlBuckets` treats a bucket's payroll-source
   * metrics as `null` (never `0`) when every project contributing to that
   * bucket has NO row for ANY `source: 'payroll'` key anywhere in the whole
   * input row set (an unmapped payroll site: the project's payroll figures
   * are unknown, not zero). A project WITH at least one payroll row anywhere
   * zero-fills normally per bucket, same as every other input metric,
   * because a specific empty month for an otherwise-mapped project is a real
   * zero (see `resolvePayrollMappedProjectIds`).
   */
  source?: "payroll";
}

/** SEQ-182. `def.unit`, defaulted to 'currency' when unset. */
export function resolveMetricUnit(def: PnlMetricDefinition): PnlUnit {
  return def.unit ?? "currency";
}

/** SEQ-182. `def.metricRole`, defaulted to 'financial' when unset. */
export function resolveMetricRole(def: PnlMetricDefinition): PnlMetricRole {
  return def.metricRole ?? "financial";
}

/** SEQ-182. `def.aggregationMethod`, defaulted per `def.kind` when unset. */
export function resolveAggregationMethod(def: PnlMetricDefinition): PnlAggregationMethod {
  if (def.aggregationMethod) return def.aggregationMethod;
  return def.kind === "calculated" ? "calculated" : "sum";
}

/**
 * SEQ-182. Whether `def`'s value is ever allowed into a financial P&L total.
 * Defaults to `true` for a currency metric, `false` otherwise, so a metric
 * only needs an explicit `financialInclusion` when it disagrees with its own
 * unit (e.g. `labour.overtime`: currency, but excluded anyway).
 */
export function resolveFinancialInclusion(def: PnlMetricDefinition): boolean {
  if (def.financialInclusion !== undefined) return def.financialInclusion;
  return resolveMetricUnit(def) === "currency";
}

/**
 * SEQ-182. Enforces by construction, at build/test time, the rules the
 * database CHECK constraints enforce at the schema level (plan section 4.2):
 * a non-currency metric can never be financially included. Throws, naming
 * every offending key, rather than returning a validation result, because
 * this is an invariant the registry itself must hold, not a runtime user
 * input to report gracefully.
 *
 * Round 2 (code review, MEDIUM): also requires every entry to declare
 * `metricRole` and `aggregationMethod` EXPLICITLY, never falling through to
 * `resolveMetricRole` / `resolveAggregationMethod`'s kind-based default. A
 * silent default is exactly how the TypeScript engine and the SQL registry
 * could quietly disagree (e.g. a `kind: 'calculated'` dollar total defaulting
 * to `aggregationMethod: 'calculated'` here while the SQL side seeds it
 * `'sum'`); requiring both sides to write the same value out loud is the fix,
 * not a smarter default.
 */
/**
 * The `aggregationMethod` values the RPC actually implements, in the same
 * order as the database CHECK constraint
 * (`pnl_metric_registry_aggregation_method_check`, widened back to all six
 * in the phase 2 migration). Kept as a runtime list, not just the
 * `PnlAggregationMethod` type, so `assertRegistryConsistent` still catches a
 * value smuggled in past the type system (an `as any` cast, or a registry
 * loaded from JSON/an external source) rather than trusting the compiler
 * alone. Exported so the migration drift test can assert the SQL CHECK
 * admits exactly this list.
 */
export const IMPLEMENTED_AGGREGATION_METHODS: readonly PnlAggregationMethod[] = ["sum", "avg", "min", "max", "latest", "calculated"];

/**
 * SEQ-180 phase 2, D10: the units for which a per-observation value has no
 * meaningful total. A metric carrying one of these can never aggregate as
 * `sum`, enforced here (TypeScript registry) and by a CHECK constraint on
 * `pnl_metric_registry` (SQL registry). Exported so the widget validator
 * applies the identical set when it derives a metric's permitted
 * aggregations.
 */
export const RATIO_UNITS: readonly PnlUnit[] = ["percent", "ratio", "currency_per_hour"];

export function assertRegistryConsistent(registry: PnlMetricDefinition[]): void {
  const violations: string[] = [];
  for (const def of registry) {
    if (def.metricRole === undefined) {
      violations.push(`${def.metricKey}: metricRole must be set explicitly (no default by kind)`);
    }
    if (def.aggregationMethod === undefined) {
      violations.push(`${def.metricKey}: aggregationMethod must be set explicitly (no default by kind)`);
    } else if (!IMPLEMENTED_AGGREGATION_METHODS.includes(def.aggregationMethod)) {
      violations.push(
        `${def.metricKey}: aggregationMethod "${def.aggregationMethod}" is not implemented by the RPC ` +
          `(the implemented methods are ${IMPLEMENTED_AGGREGATION_METHODS.map((m) => `"${m}"`).join(", ")})`,
      );
    } else if (def.kind === "calculated" && def.aggregationMethod !== "sum" && def.aggregationMethod !== "calculated") {
      // Phase 2, plan section 6.1: the five folds apply to kind 'input'
      // metrics only. A calculated metric is derived sum-then-divide from
      // its inputs and can only be tagged 'sum' (a dollar or hours total that
      // is additive across buckets) or 'calculated' (a ratio or rate that is
      // never re-aggregated). Tagging it avg/min/max/latest would promise a
      // fold neither engine executes for it.
      violations.push(
        `${def.metricKey}: aggregationMethod "${def.aggregationMethod}" is only executed for kind "input" metrics; ` +
          `a kind "calculated" metric must be "sum" (an additive total) or "calculated" (a ratio or rate)`,
      );
    }
    // Phase 2, D10: a ratio-unit metric can never aggregate as sum. This is
    // what makes a manually typed Percent safe to return to the category
    // picker: the registry itself refuses the one method that would produce
    // a meaningless total.
    if (def.aggregationMethod === "sum" && RATIO_UNITS.includes(resolveMetricUnit(def))) {
      violations.push(
        `${def.metricKey}: aggregationMethod is "sum" but unit is "${resolveMetricUnit(def)}" ` +
          `(a ${RATIO_UNITS.join("/")} metric can never be summed across buckets; use avg, min, max, latest or calculated)`,
      );
    }
    if (resolveFinancialInclusion(def) && resolveMetricUnit(def) !== "currency") {
      violations.push(
        `${def.metricKey}: financialInclusion is true but unit is "${resolveMetricUnit(def)}" ` +
          `(only a currency metric can ever be financially included)`,
      );
    }
    // Round 2 (code review, LOW): gives resolveMetricRole a real consumer
    // rather than leaving it a dead export. 'operational' is defined
    // (plan section 4.1) as "non-currency quantities"; a currency metric
    // claiming that role would be mislabeled, not merely unusual.
    if (resolveMetricRole(def) === "operational" && resolveMetricUnit(def) === "currency") {
      violations.push(
        `${def.metricKey}: metricRole is "operational" but unit is "currency" ` +
          `(operational is reserved for non-currency quantities; use "financial")`,
      );
    }
    // Round 5 (cross-registry drift test, SQL authoritative): every
    // calculated.* key is an OUTPUT of the financial P&L, never an INPUT to
    // it. `financialInclusion` marks whether an input line is allowed into a
    // financial total (`calculated.total_cost` etc read it, gated by
    // `kind === 'input'` before resolveFinancialInclusion is ever called --
    // see computePnlBuckets); it is a category error for it to be true on
    // the total itself, which is why calculated.total_income/total_cost/
    // net_profit (currency, metricRole 'financial', but still an output) must
    // be false here just like every ratio/rate calculated key already is.
    if (def.metricKey.startsWith("calculated.") && def.financialInclusion !== false) {
      violations.push(
        `${def.metricKey}: every "calculated." key must have financialInclusion === false ` +
          `(a calculated metric is an output of the financial P&L, never an input to it)`,
      );
    }
  }
  if (violations.length > 0) {
    throw new Error(`Registry consistency violated:\n${violations.join("\n")}`);
  }
}

/** All 31 metric keys from plan section 6.1, grouped for readability. */
export const PNL_METRIC_KEYS = {
  revenue: {
    constructionIncome: "revenue.construction_income",
    retention: "revenue.retention",
  },
  budget: {
    materials: "budget.materials",
  },
  expense: {
    materials: "expense.materials",
    engineering: "expense.engineering",
    handrailsStairs: "expense.handrails_stairs",
    miscMaterials: "expense.misc_materials",
    coreCosts: "expense.core_costs",
    patching: "expense.patching",
    yard: "expense.yard",
    office: "expense.office",
    miscProjects: "expense.misc_projects",
    unmapped: "expense.unmapped",
  },
  labour: {
    contractors: "labour.contractors",
    stripping: "labour.stripping",
    scaffoldContractors: "labour.scaffold_contractors",
    labourHire: "labour.labour_hire",
    baseWages: "labour.base_wages",
    extraWages: "labour.extra_wages",
    overtime: "labour.overtime",
    overtimeHours: "labour.overtime_hours",
    /** SEQ-182. Derived from payroll (matview `ordinary_hours` column), same shape as `overtimeHours`. */
    ordinaryHours: "labour.ordinary_hours",
  },
  calculated: {
    totalIncome: "calculated.total_income",
    totalCost: "calculated.total_cost",
    netProfit: "calculated.net_profit",
    profitPercentage: "calculated.profit_percentage",
    wagesPercentage: "calculated.wages_percentage",
    materialsPercentage: "calculated.materials_percentage",
    overtimePctRevenue: "calculated.overtime_pct_revenue",
    overtimePctWages: "calculated.overtime_pct_wages",
    patchingPctRevenue: "calculated.patching_pct_revenue",
    patchingPctCost: "calculated.patching_pct_cost",
    /** SEQ-182. ordinary + overtime hours. */
    totalLabourHours: "calculated.total_labour_hours",
    /** SEQ-182. overtime / (ordinary + overtime) x 100. Null when total labour hours is 0. */
    overtimeHoursPct: "calculated.overtime_hours_pct",
    /** SEQ-182. (loaded wages + other deductible labour) / total labour hours. Null when total labour hours is 0. */
    labourCostPerHour: "calculated.labour_cost_per_hour",
    /** SEQ-182. total construction income / total labour hours. Null when total labour hours is 0. */
    revenuePerLabourHour: "calculated.revenue_per_labour_hour",
    /** SEQ-182. net profit / total labour hours. Null when total labour hours is 0. */
    profitPerLabourHour: "calculated.profit_per_labour_hour",
  },
} as const;

/**
 * The default registry, matching src/hooks/usePnlCategories.ts:11-27 plus the
 * overtime and calculated keys.
 *
 * SEQ-182 Round 2 (code review, MEDIUM): every entry now carries an EXPLICIT
 * `metricRole` and `aggregationMethod`, never relying on the kind-based
 * default in `resolveMetricRole` / `resolveAggregationMethod` (those
 * resolvers still exist for a caller building an ad-hoc registry, e.g. in a
 * test). `assertRegistryConsistent` enforces this explicitness below, so the
 * TypeScript registry and the SQL registry can only agree by both saying the
 * same thing out loud, not by both happening to fall through to the same
 * default. `revenue.construction_income`, `revenue.retention` and
 * `budget.materials` are `kind: 'input'` here (they arrive as
 * `PnlAnalyticsInputRow`s, same as every other input to this engine) but are
 * explicitly `aggregationMethod: 'sum'` / `metricRole: 'financial'` per the
 * fix, matching the SQL side's same explicit values.
 */
export const DEFAULT_PNL_METRIC_REGISTRY: PnlMetricDefinition[] = [
  { metricKey: PNL_METRIC_KEYS.revenue.constructionIncome, section: "revenue", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.revenue.retention, section: "revenue", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.budget.materials, section: "budget", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  {
    metricKey: PNL_METRIC_KEYS.expense.materials,
    section: "expense",
    kind: "input",
    deductible: false,
    group: "materials",
    metricRole: "financial",
    aggregationMethod: "sum",
  },
  { metricKey: PNL_METRIC_KEYS.expense.engineering, section: "expense", kind: "input", group: "materials", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.handrailsStairs, section: "expense", kind: "input", group: "materials", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.miscMaterials, section: "expense", kind: "input", group: "materials", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.coreCosts, section: "expense", kind: "input", group: "other", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.patching, section: "expense", kind: "input", group: "other", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.yard, section: "expense", kind: "input", group: "other", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.office, section: "expense", kind: "input", group: "other", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.miscProjects, section: "expense", kind: "input", group: "other", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.expense.unmapped, section: "expense", kind: "input", group: "other", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.labour.contractors, section: "labour", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.labour.stripping, section: "labour", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.labour.scaffoldContractors, section: "labour", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.labour.labourHire, section: "labour", kind: "input", metricRole: "financial", aggregationMethod: "sum" },
  {
    metricKey: PNL_METRIC_KEYS.labour.baseWages,
    section: "labour",
    kind: "input",
    multiplierApplies: true,
    metricRole: "financial",
    aggregationMethod: "sum",
  },
  {
    metricKey: PNL_METRIC_KEYS.labour.extraWages,
    section: "labour",
    kind: "input",
    multiplierApplies: true,
    metricRole: "financial",
    aggregationMethod: "sum",
  },
  {
    // Currency, but a KeyPay-derived figure outside the typed P&L model
    // entirely (file header). financialInclusion is now explicit false to
    // match that, on top of the pre-existing deductible: false (both agree;
    // financialInclusion is what actually gates computePnlBuckets's total_cost
    // loop, deductible only matters once a metric IS financially included).
    // source: 'payroll' (Round 6): null, not 0, on a bucket where every
    // contributing project has no payroll data at all.
    metricKey: PNL_METRIC_KEYS.labour.overtime,
    section: "labour",
    kind: "input",
    deductible: false,
    metricRole: "financial",
    financialInclusion: false,
    aggregationMethod: "sum",
    source: "payroll",
  },
  {
    metricKey: PNL_METRIC_KEYS.labour.overtimeHours,
    section: "labour",
    kind: "input",
    unit: "hours",
    deductible: false,
    metricRole: "operational",
    financialInclusion: false,
    aggregationMethod: "sum",
    decimals: 1,
    source: "payroll",
  },
  // SEQ-182: derived from payroll (the matview's new `ordinary_hours`
  // column), never typed. See labour.overtimeHours above for the same shape.
  {
    metricKey: PNL_METRIC_KEYS.labour.ordinaryHours,
    section: "labour",
    kind: "input",
    unit: "hours",
    deductible: false,
    metricRole: "operational",
    financialInclusion: false,
    aggregationMethod: "sum",
    decimals: 1,
    source: "payroll",
  },
  // These three are dollar TOTALS (additive across projects/months, exactly
  // like the phase 1 KPI cards that sum them: kpi-revenue, kpi-net-profit,
  // kpi-total-cost, table-project-performance), not ratios, so
  // aggregationMethod is 'sum', not 'calculated' -- 'calculated' is reserved
  // for a metric that must never be summed across buckets (Round 2 fix 1).
  // financialInclusion is explicitly false (Round 5, matching the SQL seed):
  // these three ARE the financial total, not an input line that could be
  // double-counted into one, so "is this included in a financial total"
  // does not apply to them the way it applies to e.g. labour.base_wages.
  // computePnlBuckets never reads financialInclusion for a kind: 'calculated'
  // entry anyway (its total_cost loop only ever looks at kind: 'input'
  // rows), so this is pure cross-registry parity with the SQL side, not a
  // runtime behaviour change.
  { metricKey: PNL_METRIC_KEYS.calculated.totalIncome, section: "calculated", kind: "calculated", metricRole: "financial", financialInclusion: false, aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.calculated.totalCost, section: "calculated", kind: "calculated", metricRole: "financial", financialInclusion: false, aggregationMethod: "sum" },
  { metricKey: PNL_METRIC_KEYS.calculated.netProfit, section: "calculated", kind: "calculated", metricRole: "financial", financialInclusion: false, aggregationMethod: "sum" },
  // The seven percentages below ARE ratios: aggregationMethod 'calculated'
  // means they can never be summed (Round 2 fix 1's new, unit-independent
  // rule), but 'avg' stays permitted, matching every phase 1 widget that
  // already averages one of these (kpi-profit-percent,
  // bar-profit-percent-by-project, both overtime/patching scatters, the
  // monthly trend line).
  { metricKey: PNL_METRIC_KEYS.calculated.profitPercentage, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  { metricKey: PNL_METRIC_KEYS.calculated.wagesPercentage, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  { metricKey: PNL_METRIC_KEYS.calculated.materialsPercentage, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  { metricKey: PNL_METRIC_KEYS.calculated.overtimePctRevenue, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  { metricKey: PNL_METRIC_KEYS.calculated.overtimePctWages, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  { metricKey: PNL_METRIC_KEYS.calculated.patchingPctRevenue, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  { metricKey: PNL_METRIC_KEYS.calculated.patchingPctCost, section: "calculated", kind: "calculated", unit: "percent", metricRole: "calculated", financialInclusion: false, aggregationMethod: "calculated" },
  // ── SEQ-182: derived labour metrics (plan section 4.3) ──────────────────
  {
    // Round 3 (alignment with the SQL registry): hours are additive, so this
    // is aggregationMethod 'sum', not 'calculated', matching the SQL seed
    // exactly. Unlike the four ratios/rates below it, total_labour_hours is
    // never divided by anything to produce itself -- it is ordinary +
    // overtime, both of which are themselves plain sums -- so it can be
    // summed again across buckets (e.g. twelve monthly buckets summing to
    // the annual bucket) without the sum-then-divide hazard that keeps the
    // percent/rate metrics below pinned to 'calculated'. computePnlBuckets
    // itself already computes this as ordinaryHours + overtimeHoursValue
    // per bucket (never as a ratio), so no logic change was needed there,
    // only this registry field.
    metricKey: PNL_METRIC_KEYS.calculated.totalLabourHours,
    section: "calculated",
    kind: "calculated",
    unit: "hours",
    metricRole: "operational",
    financialInclusion: false,
    aggregationMethod: "sum",
    decimals: 1,
  },
  {
    metricKey: PNL_METRIC_KEYS.calculated.overtimeHoursPct,
    section: "calculated",
    kind: "calculated",
    unit: "percent",
    metricRole: "calculated",
    financialInclusion: false,
    aggregationMethod: "calculated",
    decimals: 2,
  },
  {
    metricKey: PNL_METRIC_KEYS.calculated.labourCostPerHour,
    section: "calculated",
    kind: "calculated",
    unit: "currency_per_hour",
    metricRole: "calculated",
    financialInclusion: false,
    aggregationMethod: "calculated",
    decimals: 2,
  },
  {
    metricKey: PNL_METRIC_KEYS.calculated.revenuePerLabourHour,
    section: "calculated",
    kind: "calculated",
    unit: "currency_per_hour",
    metricRole: "calculated",
    financialInclusion: false,
    aggregationMethod: "calculated",
    decimals: 2,
  },
  {
    metricKey: PNL_METRIC_KEYS.calculated.profitPerLabourHour,
    section: "calculated",
    kind: "calculated",
    unit: "currency_per_hour",
    metricRole: "calculated",
    financialInclusion: false,
    aggregationMethod: "calculated",
    decimals: 2,
  },
];

/**
 * The four grains `computePnlBuckets` can roll fact rows up to, client side.
 *
 * Round 3 (code review, parity harness divergence 4): this used to be named
 * `PnlGroupBy` and was reused, unchanged, as `analyticsClient.ts`'s RPC
 * request `groupBy` field. The RPC's CHECK constraint at the time
 * (supabase/migrations/20260908000500_pnl_analytics_query.sql) was
 * `project | month | quarter | calendar_year | financial_year | builder |
 * status`, with neither `project_month` nor `company`. Renamed to make plain
 * that this is a purely local, client-side rollup vocabulary, not a copy of
 * what the RPC accepts. A widget (or any caller) fetches from the RPC at a
 * grain the RPC supports, then rolls the returned rows up further with this
 * grain when it wants something the RPC itself has no concept of.
 *
 * The two vocabularies have since overlapped and are still separate types:
 * SEQ-180 phase 2 made `company` an RPC grain too, and SEQ-193
 * (20261006000100_seq193_pnl_project_month_grain.sql) made `project_month`
 * one (the Project performance table's By month view fetches it directly,
 * one RPC call, bounded to 24 months). `computePnlBuckets` still produces
 * `project_month` client side for its own callers. See `analyticsClient.ts`'s
 * `PnlGroupBy` for what the RPC accepts, a completely separate type on
 * purpose so the two cannot be confused or interchanged.
 *
 * SEQ-180 phase 2 adds three grains: `financial_year` (the Australian FY,
 * 1 July to 30 June, keyed `FY26` exactly like the RPC's `pnl_bucket_key`),
 * and `project_manager` / `site_manager` (D22, keyed by the name, with a
 * null bucket keyed and labelled "(no project manager)" /
 * "(no site manager)", mirroring the RPC's "(no builder)" rule). The two
 * manager grains read `projectPeople`, computePnlBuckets's optional fifth
 * argument; a project absent from that map falls into the null bucket.
 */
export type PnlRollupGrain =
  | "project"
  | "month"
  | "project_month"
  | "company"
  | "financial_year"
  | "project_manager"
  | "site_manager";

/** Bucket key (and label) for a project whose project manager is null or unset, matching the RPC's `(no builder)` precedent. */
export const PNL_NO_PROJECT_MANAGER_LABEL = "(no project manager)";
/** Bucket key (and label) for a project whose site manager is null or unset. */
export const PNL_NO_SITE_MANAGER_LABEL = "(no site manager)";
/** The single bucket key `computePnlBuckets` emits at `company` grain. */
export const PNL_COMPANY_BUCKET_KEY = "__company__";

/** SEQ-180 phase 2, D22: the two free-text people fields on `claims_projects`, keyed by projectId. */
export interface PnlProjectPeopleEntry {
  projectManager?: string | null;
  siteManager?: string | null;
}
export type PnlProjectPeople = Record<string, PnlProjectPeopleEntry>;

/**
 * The financial-year bucket key for an ISO 'YYYY-MM-DD' period month,
 * matching `pnl_bucket_key`'s `'FY' || right(year, 2)` exactly. Pure string
 * arithmetic on the ISO text (no Date construction), so it is timezone-free
 * and needs no date library in this engine; fiscalYear.ts's `fiscalYearOf`
 * applies the same July rule for the page's own date presets.
 */
export function financialYearBucketKey(periodMonth: string): string {
  const year = Number(periodMonth.slice(0, 4));
  const month = Number(periodMonth.slice(5, 7));
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    throw new Error(`financialYearBucketKey: "${periodMonth}" is not an ISO YYYY-MM-DD period month`);
  }
  const fy = month >= 7 ? year + 1 : year;
  return `FY${String(fy % 100).padStart(2, "0")}`;
}

/** Per-project settings a bucket needs at query time. Keyed by projectId. */
export type PnlWagesMultipliers = Record<string, number>;

/**
 * The default every other copy of this rule uses when a project has no
 * settings row, or the row's multiplier is null/zero:
 * src/hooks/usePnlMonthlyData.ts:131-138 (`Number(sd.wages_multiplier) ||
 * 1.04`), the newsletter fix (pnlProjectTotals.ts, via the same settings
 * map), and the SQL RPC (`coalesce(NULLIF(fs.wages_multiplier, 0), 1.04)`,
 * supabase/migrations/20260908000500_pnl_analytics_query.sql).
 * `computePnlBuckets` itself defaults nothing (see below): this constant only
 * lives here, so every caller resolves the same way.
 */
export const DEFAULT_WAGES_MULTIPLIER = 1.04;

/** One project's raw wages_multiplier setting, or null/undefined when the project has no settings row. */
export interface ProjectWagesMultiplierSetting {
  projectId: string;
  wagesMultiplier: number | null | undefined;
}

/**
 * Resolves a complete per-project multiplier map for exactly the given
 * project ids, applying DEFAULT_WAGES_MULTIPLIER wherever a project has no
 * settings row, or a null, undefined, or zero multiplier, in ONE place, so no
 * caller of computePnlBuckets has to reimplement the fallback (and get it
 * wrong: a naive `?? 1` default, which computePnlBuckets used to have, silently
 * disagrees with every other engine copy).
 */
export function resolveWagesMultipliers(
  settingsRows: ProjectWagesMultiplierSetting[],
  projectIds: string[],
): PnlWagesMultipliers {
  const byProject = new Map(settingsRows.map((s) => [s.projectId, s.wagesMultiplier]));
  const resolved: PnlWagesMultipliers = {};
  for (const projectId of projectIds) {
    const raw = byProject.get(projectId);
    resolved[projectId] = raw ? raw : DEFAULT_WAGES_MULTIPLIER;
  }
  return resolved;
}

export interface PnlBucketResult {
  groupKey: string;
  projectId: string | null;
  periodMonth: string | null;
  /** Every input metric folded by its own aggregationMethod, plus every calculated metric. Ratios are null on a zero denominator. */
  metrics: Record<string, number | null>;
  /**
   * SEQ-180 phase 2 (section 6.3): for every `latest` metric that has at
   * least one cell in this bucket, the ISO 'YYYY-MM-01' month its reported
   * value is a snapshot of. Empty when the bucket has no `latest` metric
   * with a value. Mirrors the RPC's per-row `metricsAsAt`.
   */
  metricsAsAt: Record<string, string>;
}

function isDeductible(def: PnlMetricDefinition): boolean {
  return def.deductible !== false;
}

function bucketKeyFor(
  groupBy: PnlRollupGrain,
  projectId: string,
  periodMonth: string,
  projectPeople: PnlProjectPeople,
): string {
  switch (groupBy) {
    case "project":
      return projectId;
    case "month":
      return periodMonth;
    case "project_month":
      return `${projectId}::${periodMonth}`;
    case "company":
      return PNL_COMPANY_BUCKET_KEY;
    case "financial_year":
      return financialYearBucketKey(periodMonth);
    case "project_manager": {
      const name = projectPeople[projectId]?.projectManager;
      return name === null || name === undefined || name.trim() === "" ? PNL_NO_PROJECT_MANAGER_LABEL : name;
    }
    case "site_manager": {
      const name = projectPeople[projectId]?.siteManager;
      return name === null || name === undefined || name.trim() === "" ? PNL_NO_SITE_MANAGER_LABEL : name;
    }
    default: {
      const exhaustive: never = groupBy;
      throw new Error(`Unknown groupBy: ${String(exhaustive)}`);
    }
  }
}

/** One cell's contribution to a non-sum fold: the summed fact rows for one (project, month) under one metric key. */
interface FoldCell {
  periodMonth: string;
  value: number;
}

/**
 * Executes one of the four non-sum folds over a bucket's cells for one
 * metric (plan section 6.2 and 6.3). Returns null when there is no cell:
 * a mean, extreme or snapshot of nothing is not zero.
 */
function foldCells(method: PnlAggregationMethod, cells: FoldCell[]): { value: number | null; asAt: string | null } {
  if (method === "calculated" || cells.length === 0) return { value: null, asAt: null };
  switch (method) {
    case "avg":
      return { value: cells.reduce((s, c) => s + c.value, 0) / cells.length, asAt: null };
    case "min":
      return { value: cells.reduce((m, c) => Math.min(m, c.value), Number.POSITIVE_INFINITY), asAt: null };
    case "max":
      return { value: cells.reduce((m, c) => Math.max(m, c.value), Number.NEGATIVE_INFINITY), asAt: null };
    case "latest": {
      // Two steps (6.3): the greatest period month among the bucket's
      // cells, then the sum of the cells AT that month. A project whose
      // last cell is earlier contributes nothing: the value is a snapshot
      // of one date, never a mix of dates.
      const latestMonth = cells.reduce((m, c) => (c.periodMonth > m ? c.periodMonth : m), cells[0].periodMonth);
      const value = cells.filter((c) => c.periodMonth === latestMonth).reduce((s, c) => s + c.value, 0);
      return { value, asAt: latestMonth };
    }
    case "sum":
      throw new Error(`foldCells: "${method}" is not a cell fold`);
    default: {
      const exhaustive: never = method;
      throw new Error(`foldCells: unknown aggregation method "${String(exhaustive)}"`);
    }
  }
}

/**
 * Sum-then-divide, returning null (never Infinity or NaN) when the
 * denominator is not greater than zero.
 */
function ratio(numerator: number, denominator: number): number | null {
  if (!(denominator > 0)) return null;
  return (numerator / denominator) * 100;
}

/**
 * SEQ-182. Plain division, returning null (never Infinity or NaN) when the
 * denominator is not greater than zero, same rule as `ratio`. Unlike `ratio`,
 * does not multiply by 100: used for a rate (e.g. dollars per hour), not a
 * percentage.
 */
function safeDivide(numerator: number, denominator: number): number | null {
  if (!(denominator > 0)) return null;
  return numerator / denominator;
}

/**
 * SEQ-182 Round 6 (browser E2E finding). Which projects have ANY payroll
 * data at all: at least one row, anywhere in the whole `rows` array (any
 * bucket, any month), for a registry key whose `source` is `'payroll'`.
 * Mapping is a whole-project/site property, not a per-metric one (an
 * "unmapped site" has no payroll data for any of its metrics, not just
 * some), so a project needs only one qualifying row to count as mapped for
 * every payroll-source key. Exported so `computePnlBuckets` and a caller
 * that wants to explain a null cell (e.g. "map this site" copy) agree on the
 * exact same definition.
 */
export function resolvePayrollMappedProjectIds(
  rows: PnlAnalyticsInputRow[],
  registry: PnlMetricDefinition[],
): Set<string> {
  const registryByKey = new Map(registry.map((def) => [def.metricKey, def]));
  const mapped = new Set<string>();
  for (const row of rows) {
    if (registryByKey.get(row.metricKey)?.source === "payroll") {
      mapped.add(row.projectId);
    }
  }
  return mapped;
}

/**
 * Aggregate fact rows into buckets at the requested grain, reproducing
 * calculateTotalsPnl's semantics: materials budget replaces the Materials
 * deduction, the wages multiplier feeds only the calculated figures (never
 * the raw `labour.base_wages` / `labour.extra_wages` values themselves,
 * Round 3), an unmapped category stays deductible, and overtime (dollars or
 * hours) never enters total_cost or net_profit.
 *
 * SEQ-180 follow-up (14 September 2026): WHICH of the two P&L tab behaviours
 * a bucket reproduces now depends on its grain. A single-month grain (`month`,
 * `project_month`) reproduces calculateMonthlyPnl, which never multiplies;
 * every aggregating grain reproduces calculateTotalsPnl, which multiplies the
 * aggregate. wagesMultiplierGrain.ts owns that decision for this engine and
 * for the RPC, and documents the additivity it knowingly gives up (monthly
 * buckets no longer sum to an aggregate bucket, by exactly
 * (multiplier - 1) x wages). Every input key from
 * `registry` is always present in `metrics`, zero-filled when a bucket has no
 * contributing rows for it, matching the RPC's `coalesce(sum(...), 0)`
 * (Round 3, parity harness divergence 5) -- EXCEPT a `source: 'payroll'`
 * metric in a bucket where every contributing project is payroll-unmapped
 * (Round 6): that reports `null`, because "we have no idea" and "this
 * project genuinely worked zero hours this month" are different facts an
 * E2E pass caught the page conflating (both rendered "0.0 hrs"). A
 * payroll-source metric still zero-fills normally for any bucket touching at
 * least one mapped project, including a mapped project's own empty month.
 * Every derived hours metric (`calculated.total_labour_hours` and the four
 * figures built from it) is `null` whenever `labour.ordinary_hours` or
 * `labour.overtime_hours` is `null` for that bucket, for the same reason.
 *
 * SEQ-180 phase 2 (plan section 6): an input metric is folded by its own
 * registry `aggregationMethod`. `sum` behaves exactly as before. `avg`,
 * `min`, `max` and `latest` fold over the bucket's CELLS (one cell per
 * (project, month) that has a fact row for that key, fact rows summed to
 * the cell first, exactly like the RPC's `raw_by_key` CTE), and report
 * `null`, never 0, when the bucket has no cell for that key. A `latest`
 * metric additionally records the month its value is a snapshot of in
 * `metricsAsAt`. The calculated dollar figures (`calculated.total_cost`
 * and friends) keep reading each financially included input's raw SUM,
 * whatever that input's own aggregationMethod is, because they are the
 * financial P&L's own sum-then-divide derivation (section 6.1).
 * `projectPeople` feeds the two manager grains only and may be omitted for
 * every other grain.
 *
 * SINGLE-ORGANISATION PRECONDITION. This engine has no organisation
 * dimension: `company` is one bucket, and no bucket key carries an
 * organisation. The RPC folds organization_id into every bucket key so a
 * caller visible to two organisations never gets a merged row; a caller
 * of this function must scope `rows` to one organisation first (the parity
 * emitter computes each organisation's buckets separately). When rows
 * carry `organizationId`, two distinct values throw rather than merge.
 */
export function computePnlBuckets(
  rows: PnlAnalyticsInputRow[],
  registry: PnlMetricDefinition[],
  wagesMultipliers: PnlWagesMultipliers,
  groupBy: PnlRollupGrain,
  projectPeople: PnlProjectPeople = {},
): PnlBucketResult[] {
  const registryByKey = new Map(registry.map((def) => [def.metricKey, def]));
  const organisations = new Set(rows.map((r) => r.organizationId).filter((id): id is string => typeof id === "string"));
  if (organisations.size > 1) {
    throw new Error(
      `computePnlBuckets: rows span ${organisations.size} organisations (${[...organisations].join(", ")}); ` +
        `this engine is single-organisation, scope the rows to one organisation before rolling up`,
    );
  }
  // SEQ-182 Round 6: computed once, up front, from the WHOLE input row set
  // (never per bucket), because payroll mapping is a project/site property,
  // not a per-bucket one.
  const payrollMappedProjectIds = resolvePayrollMappedProjectIds(rows, registry);
  // SEQ-180 follow-up: whether buckets at THIS grain carry the wages
  // multiplier at all. A single-month grain does not, so a monthly bucket
  // reads like a P&L tab month cell; every aggregating grain does, so it
  // reads like the tab's Total column. See wagesMultiplierGrain.ts for the
  // rule and for the additivity it deliberately gives up.
  const multiplierAppliesAtThisGrain = wagesMultiplierAppliesAtGrain(groupBy);

  // Phase 2: which input keys are folded over cells rather than summed. A
  // key absent from the registry is a raw passthrough and stays summed. An
  // input key tagged 'calculated' is a contradiction (nothing derives it),
  // so it is reported null, never silently summed, matching the RPC
  // (20260910000050's aggregation_method column comment).
  const foldMethodByKey = new Map<string, PnlAggregationMethod>();
  for (const def of registry) {
    if (def.kind !== "input") continue;
    const method = resolveAggregationMethod(def);
    if (method !== "sum") foldMethodByKey.set(def.metricKey, method);
  }

  // Step 1: partial sums per (bucketKey, projectId), raw, no multiplier yet.
  // The multiplier must see one project's whole contribution to a bucket
  // before it is applied, so project-month rows within the same bucket are
  // combined first. Alongside the sums, each partial keeps the per-month
  // cell values for every folded key, so a bucket spanning several months
  // of one project (grain 'project', 'financial_year', 'company', the
  // manager grains) still folds over its individual project-months.
  interface ProjectPartial {
    bucketKey: string;
    projectId: string;
    periodMonth: string | null; // set only when the bucket is single-month
    sums: Record<string, number>;
    cells: Map<string, Map<string, number>>; // metricKey -> periodMonth -> summed value
  }
  const partials = new Map<string, ProjectPartial>();

  for (const row of rows) {
    const bucketKey = bucketKeyFor(groupBy, row.projectId, row.periodMonth, projectPeople);
    const partialKey = `${bucketKey}|${row.projectId}`;
    let partial = partials.get(partialKey);
    if (!partial) {
      partial = {
        bucketKey,
        projectId: row.projectId,
        periodMonth: groupBy === "month" || groupBy === "project_month" ? row.periodMonth : null,
        sums: {},
        cells: new Map(),
      };
      partials.set(partialKey, partial);
    }
    partial.sums[row.metricKey] = (partial.sums[row.metricKey] || 0) + row.value;
    if (foldMethodByKey.has(row.metricKey)) {
      let byMonth = partial.cells.get(row.metricKey);
      if (!byMonth) {
        byMonth = new Map();
        partial.cells.set(row.metricKey, byMonth);
      }
      byMonth.set(row.periodMonth, (byMonth.get(row.periodMonth) || 0) + row.value);
    }
  }

  // Step 2: fold each project's RAW sums into its bucket untouched (so
  // `metrics[labour.base_wages]` etc. always mean the same thing the RPC's
  // response means), while separately accumulating each project's own
  // multiplier x (base_wages + extra_wages) into a bucket-level total that
  // ONLY the calculated figures in step 3 read.
  const bucketSums = new Map<string, Record<string, number>>();
  const bucketMultipliedWagesTotal = new Map<string, number>();
  const bucketMeta = new Map<string, { projectId: string | null; periodMonth: string | null }>();
  // SEQ-182 Round 6: every distinct project contributing ANY row to a
  // bucket, so step 3 can decide whether a bucket is entirely payroll-
  // unmapped (every contributing project is absent from
  // payrollMappedProjectIds) or has at least one mapped project.
  const bucketProjectIds = new Map<string, Set<string>>();
  // Phase 2: every cell of every folded key, per bucket, across all the
  // projects that contribute to it (section 6.3's "across projects").
  const bucketCells = new Map<string, Map<string, FoldCell[]>>();

  for (const partial of partials.values()) {
    const multiplier = wagesMultipliers[partial.projectId];
    if (multiplier === undefined) {
      throw new Error(
        `computePnlBuckets: no wages multiplier resolved for project "${partial.projectId}". ` +
          `Every project present in rows must have an entry in wagesMultipliers. Use ` +
          `resolveWagesMultipliers(settingsRows, projectIds) to apply the same missing/zero -> ` +
          `${DEFAULT_WAGES_MULTIPLIER} default every other engine copy uses, rather than guessing here.`,
      );
    }

    let bucket = bucketSums.get(partial.bucketKey);
    if (!bucket) {
      bucket = {};
      bucketSums.set(partial.bucketKey, bucket);
      bucketMeta.set(partial.bucketKey, {
        projectId: groupBy === "project" || groupBy === "project_month" ? partial.projectId : null,
        periodMonth: partial.periodMonth,
      });
    }
    let projectIds = bucketProjectIds.get(partial.bucketKey);
    if (!projectIds) {
      projectIds = new Set<string>();
      bucketProjectIds.set(partial.bucketKey, projectIds);
    }
    projectIds.add(partial.projectId);
    for (const [metricKey, value] of Object.entries(partial.sums)) {
      bucket[metricKey] = (bucket[metricKey] || 0) + value; // raw, never multiplied
    }
    let cellsForBucket = bucketCells.get(partial.bucketKey);
    if (!cellsForBucket) {
      cellsForBucket = new Map();
      bucketCells.set(partial.bucketKey, cellsForBucket);
    }
    for (const [metricKey, byMonth] of partial.cells) {
      let cells = cellsForBucket.get(metricKey);
      if (!cells) {
        cells = [];
        cellsForBucket.set(metricKey, cells);
      }
      for (const [periodMonth, value] of byMonth) {
        cells.push({ periodMonth, value });
      }
    }

    let projectMultiplierAppliesRaw = 0;
    for (const [metricKey, def] of registryByKey) {
      if (def.multiplierApplies && metricKey in partial.sums) {
        projectMultiplierAppliesRaw += partial.sums[metricKey];
      }
    }
    const existing = bucketMultipliedWagesTotal.get(partial.bucketKey) || 0;
    const effectiveMultiplier = multiplierAppliesAtThisGrain ? multiplier : 1;
    bucketMultipliedWagesTotal.set(partial.bucketKey, existing + projectMultiplierAppliesRaw * effectiveMultiplier);
  }

  // Step 3: derive the ten calculated.* metrics per bucket from the summed inputs.
  const results: PnlBucketResult[] = [];
  for (const [bucketKey, sums] of bucketSums) {
    const meta = bucketMeta.get(bucketKey)!;
    // SEQ-182 Round 6: true only when NOT ONE project touching this bucket
    // has any payroll data anywhere in the whole input set.
    const bucketProjectIdsForThisBucket = bucketProjectIds.get(bucketKey) ?? new Set<string>();
    const bucketIsPayrollUnmapped =
      bucketProjectIdsForThisBucket.size > 0 &&
      [...bucketProjectIdsForThisBucket].every((projectId) => !payrollMappedProjectIds.has(projectId));

    // Zero-fill every registry `sum` input key first, so a bucket with no
    // contributing rows for a key still reports 0, not a missing key --
    // EXCEPT a source: 'payroll' key on a bucket that is entirely
    // payroll-unmapped, which reports null instead (Round 6): "no payroll
    // data at all" and "worked zero hours" are different facts, and only
    // the latter is a real zero. Phase 2 (section 6.2): an avg, min, max or
    // latest input key is folded over the bucket's cells instead, and
    // reports null (never 0) when it has no cell, because a mean of nothing
    // is not zero. The payroll null rule wins for a payroll-source key
    // either way, so "unmapped" can never read as a real observation.
    const metrics: Record<string, number | null> = {};
    const metricsAsAt: Record<string, string> = {};
    const cellsForBucket = bucketCells.get(bucketKey) ?? new Map<string, FoldCell[]>();
    for (const def of registry) {
      if (def.kind !== "input") continue;
      if (def.source === "payroll" && bucketIsPayrollUnmapped) {
        metrics[def.metricKey] = null;
        continue;
      }
      const foldMethod = foldMethodByKey.get(def.metricKey);
      if (foldMethod === undefined) {
        metrics[def.metricKey] = sums[def.metricKey] || 0;
        continue;
      }
      const folded = foldCells(foldMethod, cellsForBucket.get(def.metricKey) ?? []);
      metrics[def.metricKey] = folded.value;
      if (folded.asAt !== null) metricsAsAt[def.metricKey] = folded.asAt;
    }
    // Preserve any fact-row metric key that is not (yet) in the registry
    // (e.g. an unregistered custom category), raw, so no data is silently
    // dropped just because a caller passed a partial registry.
    for (const [metricKey, value] of Object.entries(sums)) {
      if (!(metricKey in metrics)) {
        metrics[metricKey] = value;
      }
    }

    const totalIncome = sums[PNL_METRIC_KEYS.revenue.constructionIncome] || 0;
    const multipliedWages = bucketMultipliedWagesTotal.get(bucketKey) || 0;

    let totalCost = sums[PNL_METRIC_KEYS.budget.materials] || 0;
    let otherDeductibleLabour = 0; // labour-section deductible inputs that are NOT multiplierApplies (raw)
    let materialsActual = 0;

    for (const [metricKey, def] of registryByKey) {
      if (def.kind !== "input") continue;
      // SEQ-182: gated by financialInclusion, not by unit or key name, so a
      // non-currency metric is excluded by construction (assertRegistryConsistent
      // enforces that financialInclusion can never be true for one anyway).
      // Excludes labour.overtime_hours and labour.ordinary_hours.
      if (!resolveFinancialInclusion(def)) continue;
      if (def.section !== "expense" && def.section !== "labour") continue;
      const value = sums[metricKey] || 0;

      if (def.section === "labour") {
        if (def.multiplierApplies) continue; // handled via multipliedWages above instead of the raw value
        if (isDeductible(def)) {
          otherDeductibleLabour += value; // excludes labour.overtime (deductible: false)
        }
        continue;
      }

      // section === 'expense'
      if (isDeductible(def)) {
        totalCost += value; // excludes expense.materials (deductible: false)
      }
      if (def.group === "materials" && isDeductible(def)) {
        materialsActual += value;
      }
    }

    const wagesTotal = multipliedWages + otherDeductibleLabour;
    totalCost += wagesTotal;

    const netProfit = totalIncome - totalCost;
    // Round 7 (parity divergence, TS was wrong): `labour.overtime` is
    // `source: 'payroll'`, same as the two hours keys, so it must be read
    // from the null-aware `metrics` map, not `sums` -- reading `sums[...] ||
    // 0` here silently turned "we have no payroll data for this bucket"
    // into "this bucket had zero dollars of overtime", the exact bug class
    // Round 6 fixed for the hours metrics but missed for this pre-existing
    // (SEQ-180) pair of ratios. `patchingDollars` and `materialsPercentBase`
    // are NOT payroll-source (expense.patching, budget.materials), so they
    // keep reading from `sums` unchanged.
    const overtimeDollars = metrics[PNL_METRIC_KEYS.labour.overtime];
    const patchingDollars = sums[PNL_METRIC_KEYS.expense.patching] || 0;
    const materialsPercentBase = (sums[PNL_METRIC_KEYS.budget.materials] || 0) + materialsActual;

    metrics[PNL_METRIC_KEYS.calculated.totalIncome] = totalIncome;
    metrics[PNL_METRIC_KEYS.calculated.totalCost] = totalCost;
    metrics[PNL_METRIC_KEYS.calculated.netProfit] = netProfit;
    metrics[PNL_METRIC_KEYS.calculated.profitPercentage] = ratio(netProfit, totalIncome);
    metrics[PNL_METRIC_KEYS.calculated.wagesPercentage] = ratio(wagesTotal, totalIncome);
    metrics[PNL_METRIC_KEYS.calculated.materialsPercentage] = ratio(materialsPercentBase, totalIncome);
    metrics[PNL_METRIC_KEYS.calculated.overtimePctRevenue] = overtimeDollars === null ? null : ratio(overtimeDollars, totalIncome);
    metrics[PNL_METRIC_KEYS.calculated.overtimePctWages] = overtimeDollars === null ? null : ratio(overtimeDollars, wagesTotal);
    metrics[PNL_METRIC_KEYS.calculated.patchingPctRevenue] = ratio(patchingDollars, totalIncome);
    metrics[PNL_METRIC_KEYS.calculated.patchingPctCost] = ratio(patchingDollars, totalCost);

    // ── SEQ-182: derived labour metrics (plan section 4.3) ────────────────
    // Read from `metrics`, not `sums`: `metrics[...ordinaryHours]` /
    // `metrics[...overtimeHours]` are already the payroll-null-aware values
    // computed by the zero-fill loop above (Round 6), so a fully
    // payroll-unmapped bucket's `null` propagates into every metric derived
    // from hours, rather than being silently treated as `0 + 0 = 0`. When
    // both inputs ARE numbers, total labour hours is a plain sum, always
    // defined (0, not null, when a MAPPED project genuinely worked zero
    // hours this bucket). The four rates derived from it use the same
    // null-on-zero-denominator rule as every other ratio in this function.
    const ordinaryHours = metrics[PNL_METRIC_KEYS.labour.ordinaryHours];
    const overtimeHoursValue = metrics[PNL_METRIC_KEYS.labour.overtimeHours];
    const totalLabourHours = ordinaryHours === null || overtimeHoursValue === null ? null : ordinaryHours + overtimeHoursValue;

    metrics[PNL_METRIC_KEYS.calculated.totalLabourHours] = totalLabourHours;
    metrics[PNL_METRIC_KEYS.calculated.overtimeHoursPct] =
      totalLabourHours === null ? null : ratio(overtimeHoursValue as number, totalLabourHours);
    // wagesTotal is the same "loaded wages + other deductible labour"
    // figure calculated.wages_percentage already uses as its numerator, so
    // labour cost per hour cannot drift from the wages percentage shown
    // beside it.
    metrics[PNL_METRIC_KEYS.calculated.labourCostPerHour] =
      totalLabourHours === null ? null : safeDivide(wagesTotal, totalLabourHours);
    metrics[PNL_METRIC_KEYS.calculated.revenuePerLabourHour] =
      totalLabourHours === null ? null : safeDivide(totalIncome, totalLabourHours);
    metrics[PNL_METRIC_KEYS.calculated.profitPerLabourHour] =
      totalLabourHours === null ? null : safeDivide(netProfit, totalLabourHours);

    results.push({
      groupKey: bucketKey,
      projectId: meta.projectId,
      periodMonth: meta.periodMonth,
      metrics,
      metricsAsAt,
    });
  }

  return results;
}
